import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useOnClickOutside } from 'Shared/util/custom-hooks';
import styles from './base-modal.css';


const options = {
  reserveScrollBarGap: true,
};


const BaseModal = ({
  isModalOpen,
  closeModal,
  headerText,
  content,
  buttons,
  disableClickOutside,
  className,
}) => {
  const [contentContainerNode, setContentContainerNode] = useState(null);
  const contentContainerCallbackRef = useCallback((node) => setContentContainerNode(node), []);
  const modalRef = useRef();
  useOnClickOutside(modalRef, () => !disableClickOutside && closeModal());

  useEffect(() => {
    if (!contentContainerNode) return undefined;
    if (isModalOpen) {
      disableBodyScroll(contentContainerNode, options);
    } else {
      enableBodyScroll(contentContainerNode);
    }

    return () => enableBodyScroll(contentContainerNode);
  }, [isModalOpen, contentContainerNode]);

  const modal = isModalOpen && (
    <div
      id="base-modal" /* Note: 'id' is used by <Sidebar /> */
      className={styles.overlay}
    >
      <div
        ref={modalRef}
        className={`${styles.modal} ${className || ''}`}
      >
        {headerText && (
          <div className={styles.headerContainer}>
            <h3>{headerText}</h3>
          </div>
        )}
        {content && (
          <div
            ref={contentContainerCallbackRef}
            className={styles.contentContainer}
          >
            {content}
          </div>
        )}
        {buttons && (
          <div className={styles.buttonsContainer}>
            {buttons.reverse().map((button, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={styles.buttonContainer} key={i}>
                {button}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

BaseModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  headerText: PropTypes.string,
  content: PropTypes.node,
  buttons: PropTypes.array,
  disableClickOutside: PropTypes.bool,
  className: PropTypes.string,
};

export default BaseModal;
